import React, { useEffect, useRef, useState } from 'react';
import {
  DEFAULT_LNAT_HIGHLIGHT_COLOR,
  LNAT_HIGHLIGHT_COLOR_IDS,
  LNAT_HIGHLIGHT_SWATCHES,
  getLnatHighlightFill,
} from '../constants/lnatPearsonHighlightTokens';

function preventSelectionCollapse(event) {
  event.preventDefault();
}

function HighlighterIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 20h4l10-10a2.8 2.8 0 0 0-4-4L4 16v4z" />
      <path d="M14 6l4 4" />
    </svg>
  );
}

function StrikeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 20l10-14" />
      <path d="M14 6h6" />
      <line x1="3" y1="14" x2="21" y2="14" />
    </svg>
  );
}

export default function LnatPearsonHighlightToolbar({
  activeColor = DEFAULT_LNAT_HIGHLIGHT_COLOR,
  colorScheme,
  onHighlight,
  onStrikethrough,
  onRemoveHighlight,
  onChangeColor,
  chromeFg = '#FFFFFF',
}) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const rootRef = useRef(null);
  const chipRef = useRef(null);
  const [palettePos, setPalettePos] = useState({ top: 0, left: 0 });
  const chipFill = getLnatHighlightFill(activeColor, colorScheme);

  const openPalette = () => {
    const el = chipRef.current;
    if (el && typeof el.getBoundingClientRect === 'function') {
      const box = el.getBoundingClientRect();
      setPalettePos({ top: box.bottom, left: box.left });
    }
    setPaletteOpen(true);
  };

  useEffect(() => {
    if (!paletteOpen) return undefined;
    const onDoc = (event) => {
      if (rootRef.current && rootRef.current.contains(event.target)) return;
      setPaletteOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setPaletteOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [paletteOpen]);

  const toolBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'transparent',
    border: 'none',
    color: chromeFg,
    fontFamily: 'Tahoma, Arial, Helvetica, sans-serif',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    padding: '0 8px',
    height: '100%',
  };

  return (
    <div
      ref={rootRef}
      className="lnat-pearson-highlight-toolbar"
      style={{ display: 'inline-flex', alignItems: 'stretch', height: '100%', position: 'relative' }}
    >
      <button
        type="button"
        ref={chipRef}
        aria-label="Highlight colour"
        aria-expanded={paletteOpen}
        aria-haspopup="true"
        onMouseDown={preventSelectionCollapse}
        onClick={openPalette}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '0 6px',
          height: '100%',
          color: chromeFg,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 18,
            height: 18,
            background: chipFill,
            border: '1px solid #1a1a1a',
            display: 'inline-block',
            boxSizing: 'border-box',
          }}
        />
        <span aria-hidden="true" style={{ fontSize: 10, lineHeight: 1 }}>▼</span>
      </button>
      <button
        type="button"
        aria-label="Highlight (J)"
        onMouseDown={preventSelectionCollapse}
        onClick={() => { if (typeof onHighlight === 'function') onHighlight(); }}
        style={toolBtn}
      >
        <HighlighterIcon />
        Highlight (J)
      </button>
      <button
        type="button"
        aria-label="Strikethrough (W)"
        onMouseDown={preventSelectionCollapse}
        onClick={() => { if (typeof onStrikethrough === 'function') onStrikethrough(); }}
        style={toolBtn}
      >
        <StrikeIcon />
        Strikethrough (W)
      </button>
      {paletteOpen ? (
        <div
          className="lnat-pearson-highlight-palette"
          style={{
            position: 'fixed',
            top: palettePos.top,
            left: palettePos.left,
            zIndex: 10350,
            background: '#083A6B',
            border: '2px solid #0A2A4A',
            padding: 8,
            minWidth: 168,
            boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
          }}
        >
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            {LNAT_HIGHLIGHT_COLOR_IDS.map((id) => {
              const swatch = LNAT_HIGHLIGHT_SWATCHES[id];
              return (
                <button
                  key={id}
                  type="button"
                  aria-label={swatch.label}
                  onMouseDown={preventSelectionCollapse}
                  onClick={() => {
                    if (typeof onChangeColor === 'function') onChangeColor(id);
                    setPaletteOpen(false);
                  }}
                  style={{
                    width: 22,
                    height: 22,
                    background: swatch.hex,
                    border: '1px solid #111',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                />
              );
            })}
          </div>
          <button
            type="button"
            aria-label="Remove Highlight"
            onMouseDown={preventSelectionCollapse}
            onClick={() => {
              if (typeof onRemoveHighlight === 'function') onRemoveHighlight();
              setPaletteOpen(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              background: 'transparent',
              border: 'none',
              color: '#FFFFFF',
              fontFamily: 'Tahoma, Arial, Helvetica, sans-serif',
              fontSize: 13,
              cursor: 'pointer',
              padding: 0,
              textAlign: 'left',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 22,
                height: 22,
                background: '#FFFFFF',
                border: '1px solid #111',
                boxSizing: 'border-box',
                flex: 'none',
              }}
            />
            Remove Highlight
          </button>
        </div>
      ) : null}
    </div>
  );
}
